import clsx from 'clsx';
import styles from './styles.module.scss';
import { useOrientation } from '@/hooks';
import { useShopStore } from '@/stores';
import { useTranslation } from 'react-i18next';
import { useShopBuildingOptions, useShopFloorList } from '@/hooks/poi/useShopData';
import { SHOP_SEARCH_MODE } from '@/enums/shop';
import type { FloorInfo } from '@/services/dto/kiosk-config';
import ShopFnbFacilityFloorButton from '@/components/common/shop-fnb-facility/buttons/floor';
import ShopFnbFacilityBuildingDropdown from '@/components/common/shop-fnb-facility/building-dropdown';
import { AppUtils } from '@/utils/app';

const ShopSearchByFloor = () => {
    const { t } = useTranslation();
    const { isHorizontal } = useOrientation();
    const selectedFloor = useShopStore((state) => state.selectedFloor);
    const selectedBuildingId = useShopStore((state) => state.selectedBuildingId);
    const setSelectedFloor = useShopStore((state) => state.setSelectedFloor);
    const setSelectedBuildingId = useShopStore((state) => state.setSelectedBuildingId);
    const updateShopContents = useShopStore((state) => state.updateShopContents);

    const buildingOptions = useShopBuildingOptions(t);
    const floorList = useShopFloorList(selectedBuildingId, t);

    const handleSelectFloor = (floor: { id: string; label: string; value: string; data: FloorInfo | null | string[] }) => {
        setSelectedFloor(floor as any);
        updateShopContents(SHOP_SEARCH_MODE.SEARCH_BY_FLOOR);
    };

    const handleSelectBuilding = (buildingId: string) => {
        setSelectedBuildingId(buildingId);
        handleSelectFloor(floorList[0]);
    };

    return (
        <div
            className={clsx(styles['search-by-floor'], {
                [styles['search-by-floor--vertical']]: !isHorizontal,
                ['ml-0! justify-center!']: buildingOptions.length === 1,
            })}
        >
            <div
                className={clsx(
                    styles['search-by-floor__floors'],
                    { [styles['search-by-floor__floors--vertical']]: !isHorizontal },
                    'scrollbar-custom-gray-99938f scrollbar-w-4 overflow-y-auto'
                )}
                data-region-nav
            >
                {floorList.map((floor) => {
                    return (
                        <ShopFnbFacilityFloorButton
                            tabIndex={9}
                            data-interactive
                            role="radio"
                            type="button"
                            key={floor.id}
                            classes={floor.id === 'all' ? 'text' : 'number'}
                            aria-label={
                                floor.id === 'all'
                                    ? t('shop.search.tts.content.all_floors')
                                    : t('shop.search.tts.content.floor', { floor: AppUtils.getFloorTTSText(floor.label) })
                            }
                            title={floor.label}
                            aria-checked={floor.id === selectedFloor?.id}
                            onClick={() => handleSelectFloor(floor)}
                            isVertical={!isHorizontal}
                        />
                    );
                })}
            </div>
            {buildingOptions.length > 1 && (
                <div className={styles['search-by-floor__dropdown']}>
                    <ShopFnbFacilityBuildingDropdown defaultValue={selectedBuildingId || 'all'} options={buildingOptions} onSelect={handleSelectBuilding} />
                </div>
            )}
        </div>
    );
};

export default ShopSearchByFloor;
